import { v4 } from "uuid";
import { Step, Workflow, WorkflowExecutionLog, WorkflowGraph, WorkflowTestFilesResponse } from "../models/Workflows"; // Added WorkflowExecutionLog import
import axios from "axios";

// Define the interface for the paginated response
export interface PaginatedWorkflowExecutionLogsResponse {
    logs: WorkflowExecutionLog[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export const getAllWorkflows = async (accessToken: string): Promise<Workflow[]> => {
    try {
        const response = await axios.get(process.env.REACT_APP_API_URL + `/v1/workflows`, {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4()
            }
        });

        if (response.status !== 200) {
            throw new Error("failed to get workflows")
        }

        return response.data as Workflow[];
    }catch (error: any) {
        console.error("error getting workflows", error)
        throw new Error(error?.response?.data?.message || '')
    }
}

export const createWorkflow = async (accessToken: string, name: string, steps: Step[]): Promise<string> => {
    const response = await axios.post(process.env.REACT_APP_API_URL + `/v1/workflows`,
        {
            name,
            steps
        },
        {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4()
            }
        }
    );

    if (response.status !== 201) {
        console.error("failed to create workflow", response.data);
        throw new Error("failed to create workflow")
    }

    return response.data.id as string;
}

export const createStep = async (accessToken: string, workflowID: string, step: Step): Promise<void> => {
    try {
        const response = await axios.post(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}/steps`, step, {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4()
            }
        });

        if (response.status !== 201) {
            console.log("failed to create step", response)
            throw new Error(response.data.message)
        }
    } catch (error: any) {
        console.error("error creating step", error)
        throw new Error(error?.response?.data?.message || '')
    }
}

export const updateWorkflow = async (accessToken: string, workflowID: string, name: string, enabled: boolean): Promise<void> => {
    try {
        const response = await axios.put(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}`, {
            name,
            enabled
        }, {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4()
            },
        });

        if (response.status !== 204) {
            throw new Error("failed to get workflows")
        }
    } catch (error: any) {
        console.error("error updating workflow", error)
        throw new Error(error?.response?.data?.message || '')
    }
}

export const updateStep = async (accessToken: string, workflowID: string, stepID: string, step: Step): Promise<void> => {
    try {
        const response = await axios.put(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}/steps/${stepID}`, step, {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4()
            },
        });

        if (response.status !== 204) {
            console.error("error updating step", response.data);
            throw new Error("failed to update step")
        }
    } catch (error: any) {
        console.error("error updating step", error)
        throw new Error(error?.response?.data?.message || '')
    }
}

export const deleteStep = async (accessToken: string, workflowID: string, stepID: string): Promise<void> => {
    const response = await axios.delete(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}/steps/${stepID}`, {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        },
    });

    if (response.status !== 204) {
        throw new Error("failed to get workflows")
    }
}

export const deleteWorkflow = async (accessToken: string, workflowID: string): Promise<void> => {
    const response = await axios.delete(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}`, {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        },
    });

    if (response.status !== 204) {
        console.error("failed to delete workflow", response.data)
        throw new Error("failed to get workflows")
    }
}

export const getWorkflow = async (accessToken: string, workflowID: string): Promise<Workflow> => {
    const response = await axios.get(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}`, {
        headers: {
            'Authorization': accessToken,
            'X-Request-ID': v4()
        },
    });

    if (response.status !== 200) {
        console.error("failed to get workflow by id", response.data)
        throw new Error("failed to get workflows")
    }

    return response.data as Workflow;
}

export const updateWorkflowGraph = async (
    accessToken: string,
    workflowID: string,
    graph: WorkflowGraph
): Promise<WorkflowGraph> => {
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/v1/workflows/${workflowID}/graph`,
            graph,
            {
                headers: {
                    'Authorization': accessToken,
                    'X-Request-ID': v4(),
                    'Content-Type': 'application/json',
                },
            }
        );
        if (response.status !== 200) {
            throw new Error(response.data?.message || 'Failed to save workflow paths');
        }
        return response.data as WorkflowGraph;
    } catch (error: any) {
        console.error('error updating workflow graph', error);
        throw new Error(error?.response?.data?.message || 'Failed to save workflow paths');
    }
}

export const getWorkflowTestFiles = async (
    accessToken: string,
    workflowID: string,
    search: string,
    offset = 0,
    limit = 20,
): Promise<WorkflowTestFilesResponse> => {
    try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/v1/workflows/${workflowID}/test-files`, {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4(),
            },
            params: { search: search || undefined, offset, limit },
        });
        return response.data as WorkflowTestFilesResponse;
    } catch (error: any) {
        console.error("error getting workflow test files", error);
        throw new Error(error?.response?.data?.message || 'Failed to load compatible catalogue files');
    }
}

export const testWorkflow = async(accessToken: string, workflowID: string, fileID: string): Promise<void> => {
    try {
        const response = await axios.post(process.env.REACT_APP_API_URL + `/v1/workflows/${workflowID}/test`, { fileId: fileID }, {
            headers: {
                'Authorization': accessToken,
                'X-Request-ID': v4()
            },
        });

        if (response.status !== 202) {
            console.error("failed to test workflow", response.data)
            throw new Error("failed to get workflows")
        }

    } catch (error: any) {
        console.error("error testing workflow", error)
        throw new Error(error?.response?.data?.message || '')
    }
}

export const getWorkflowExecutionLogsPaginated = async (
    accessToken: string,
    workflowID: string,
    page: number,
    pageSize: number
): Promise<PaginatedWorkflowExecutionLogsResponse> => {
    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/v1/workflows/${workflowID}/execution_logs`, // Updated endpoint
            {
                headers: {
                    Authorization: accessToken,
                    'X-Request-ID': v4(),
                },
                params: { // Add pagination query parameters
                    page,
                    page_size: pageSize,
                },
            }
        );

        if (response.status !== 200) {
            console.error("failed to get paginated workflow execution logs", response.data);
            throw new Error(response.data?.message || "Failed to get paginated workflow execution logs");
        }

        // Assuming the backend returns data in the shape of PaginatedWorkflowExecutionLogsResponse
        // If the backend uses different field names (e.g., "items" instead of "logs", "count" instead of "total"),
        // you might need to map them here. For example:
        // return {
        //     logs: response.data.items as WorkflowExecutionLog[],
        //     total: response.data.count,
        //     page: response.data.page,
        //     pageSize: response.data.pageSize
        // };
        return response.data as PaginatedWorkflowExecutionLogsResponse;
    } catch (error: any) {
        console.error("error getting paginated workflow execution logs", error);
        throw new Error(error?.response?.data?.message || 'An unexpected error occurred while fetching paginated execution logs.');
    }
};

export const getWorkflowExecutionCount = async (): Promise<number> => {
    try {
        const response = await axios.get(process.env.REACT_APP_API_URL + `/v1/workflows/exec_count`, {});

        if (response.status !== 200) {
            console.error("failed to get workflow execution count", response.data)
            throw new Error("failed to get workflows")
        }

        return response.data as number;
    } catch (error: any) {
        console.error("error getting workflow execution count", error)
        throw new Error(error?.response?.data?.message || '')
    }
}

export const retryWorkflowExecution = (accessToken: string, workflowId: string, executionLogId: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/v1/workflows/${workflowId}/executions/${executionLogId}/retry`, {}, {
                headers: {
                    'Authorization': accessToken,
                    'X-Request-ID': v4(),
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 202) {
                resolve();
            } else {
                // Attempt to get a meaningful error message from the response
                const errorMessage = response.data?.message || `Error ${response.status}: Failed to retry workflow.`;
                reject(new Error(errorMessage));
            }
        } catch (error: any) {
            console.error('Error retrying workflow execution:', error);
            // Attempt to get a meaningful error message from the axios error object
            const message = error.response?.data?.message || error.message || 'An unexpected error occurred while retrying the workflow.';
            reject(new Error(message));
        }
    });
};
